import React, { useState } from "react";
import {
  ArrowUpRight,
  Github,
  ExternalLink,
  Calendar,
  Users,
  Star,
  Eye,
} from "lucide-react";
import { Project } from "../sample.data";
import { getVisibleSortedItems, getFeaturedItems } from "../utils";

interface ProjectsGridProps {
  projects: Project[];
  showFeaturedOnly?: boolean;
}

export const ProjectsGrid: React.FC<ProjectsGridProps> = ({
  projects,
  showFeaturedOnly = false,
}) => {
  const [hoveredProject, setHoveredProject] = useState<string | null>(null);

  const visibleProjects = showFeaturedOnly
    ? getFeaturedItems(projects)
    : getVisibleSortedItems(projects);

  if (visibleProjects.length === 0) return null;

  const getStatusConfig = (status?: string) => {
    switch (status) {
      case "completed":
        return {
          color: "from-emerald-500 to-green-600",
          bg: "bg-emerald-50",
          text: "text-emerald-700",
          dot: "bg-emerald-500",
          label: "Completed",
        };
      case "in-progress":
        return {
          color: "from-blue-500 to-indigo-600",
          bg: "bg-blue-50",
          text: "text-blue-700",
          dot: "bg-blue-500",
          label: "In Progress",
        };
      default:
        return {
          color: "from-gray-400 to-gray-500",
          bg: "bg-gray-50",
          text: "text-gray-700",
          dot: "bg-gray-400",
          label: "Planned",
        };
    }
  };

  return (
    <div className="grid gap-6 sm:gap-8 sm:grid-cols-2">
      {visibleProjects.map((project) => {
        const statusConfig = getStatusConfig(project.status);
        const isHovered = hoveredProject === project.id;

        return (
          <article
            key={project.id}
            className="group relative overflow-hidden rounded-2xl bg-white/90 backdrop-blur-xl border border-white/20 hover:bg-white/95 transition-all duration-700 hover:shadow-2xl hover:shadow-purple-500/10"
            onMouseEnter={() => setHoveredProject(project.id)}
            onMouseLeave={() => setHoveredProject(null)}
          >
            {/* Gradient Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50/50 via-transparent to-pink-50/50 opacity-0 group-hover:opacity-100 transition-opacity duration-700" />

            {/* Featured Badge */}
            {project.featured && (
              <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-full text-xs font-medium shadow-lg">
                <Star className="h-2.5 w-2.5 sm:h-3 sm:w-3 fill-current" />
                <span className="hidden sm:inline">Featured</span>
              </div>
            )}

            {/* Project Image */}
            {project.images && project.images.length > 0 && (
              <div className="relative h-40 sm:h-48 overflow-hidden">
                <img
                  src={project.images[0]}
                  alt={project.name}
                  className={`w-full h-full object-cover transition-all duration-700 ${
                    isHovered ? "scale-110" : "scale-100"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />

                {/* Image Overlay Actions */}
                <div
                  className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-300 ${
                    isHovered ? "opacity-100" : "opacity-0"
                  }`}
                >
                  <div className="flex gap-3">
                    {project.liveUrl && (
                      <button className="p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors duration-200">
                        <Eye className="h-5 w-5" />
                      </button>
                    )}
                    {project.githubUrl && (
                      <button className="p-3 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors duration-200">
                        <Github className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="relative p-4 sm:p-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4 mb-4">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-lg sm:text-xl text-gray-900 leading-tight mb-2">
                    {project.name}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-sm">
                    {project.description}
                  </p>
                </div>

                {/* Status Indicator */}
                {project.status && (
                  <div
                    className={`flex items-center gap-2 px-2 sm:px-3 py-1 sm:py-1.5 ${statusConfig.bg} rounded-full self-start`}
                  >
                    <div
                      className={`h-2 w-2 rounded-full ${statusConfig.dot} animate-pulse`}
                    />
                    <span
                      className={`text-xs font-medium ${statusConfig.text}`}
                    >
                      {statusConfig.label}
                    </span>
                  </div>
                )}
              </div>

              {/* Project Timeline */}
              {project.startDate && (
                <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-xl">
                  <Calendar className="h-4 w-4 text-gray-500" />
                  <span className="text-sm text-gray-700 font-medium">
                    {project.startDate}{" "}
                    {project.endDate && `– ${project.endDate}`}
                  </span>
                </div>
              )}

              {/* Collaborators */}
              {project.collaborators && project.collaborators.length > 0 && (
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-purple-500" />
                    <span className="text-sm font-medium text-gray-900">
                      Team
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {project.collaborators.map((collaborator) => (
                      <span
                        key={collaborator}
                        className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-medium border border-purple-200"
                      >
                        {collaborator}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Technology Stack */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <div className="h-4 w-4 rounded bg-gradient-to-r from-blue-500 to-purple-600" />
                  <span className="text-sm font-medium text-gray-900">
                    Technology Stack
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {project.tags.map((tag, index) => (
                    <span
                      key={tag}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-200 hover:scale-105 ${
                        index % 4 === 0
                          ? "bg-blue-50 text-blue-700 border border-blue-200"
                          : index % 4 === 1
                            ? "bg-purple-50 text-purple-700 border border-purple-200"
                            : index % 4 === 2
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                              : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-100">
                {project.liveUrl && (
                  <a
                    href={project.liveUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg hover:shadow-blue-500/25 hover:-translate-y-0.5 text-sm"
                  >
                    <ExternalLink className="h-4 w-4" />
                    Live Demo
                  </a>
                )}
                {project.githubUrl && (
                  <a
                    href={project.githubUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={`flex items-center justify-center gap-2 px-4 py-3 bg-gray-900 text-white rounded-xl font-medium transition-all duration-200 hover:bg-gray-800 hover:shadow-lg hover:-translate-y-0.5 text-sm ${
                      project.liveUrl ? "flex-1 sm:flex-shrink-0" : "flex-1"
                    }`}
                  >
                    <Github className="h-4 w-4" />
                    {project.liveUrl ? "Code" : "Source Code"}
                  </a>
                )}
                {project.link && !project.liveUrl && !project.githubUrl && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-600 text-white rounded-xl font-medium transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25 hover:-translate-y-0.5 text-sm"
                  >
                    <ArrowUpRight className="h-4 w-4" />
                    View Project
                  </a>
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};
